import { useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { AdminApi } from "@/api";
import { rememberSearch } from "@/components/layout/lastSearch";
import { cn } from "@/lib/utils";
import { ReferenceGenerateTab } from "./ReferenceGenerateTab";
import { ReferencePassedTab } from "./ReferencePassedTab";
import { ReferenceReviewTab } from "./ReferenceReviewTab";
import { REFERENCE_ROUTE, type ReferenceSearch, type ReferenceView } from "./referenceReview";

export { POLL_MS } from "./ReferenceGenerateTab";

const TABS: Array<{ view: ReferenceView; label: string }> = [
  { view: "generate", label: "생성" },
  { view: "review", label: "검토" },
  { view: "passed", label: "통과 모음" },
];

/**
 * Test Cases → Reference Answer: 생성 (make new versions), 검토 (pass or send
 * back each version; only a passed one is scored) and 통과 모음 (the passed
 * answers). The tab and the TC open on 검토 live in the URL.
 */
export function ReferenceAnswerPage({
  api,
  reloadKey,
  prodAccess,
}: {
  api: AdminApi;
  reloadKey: number;
  /** Null while SAMS has not said; prod TCs are not held back then. */
  prodAccess: boolean | null;
}) {
  const search = useSearch({ from: REFERENCE_ROUTE });
  const navigate = useNavigate();
  useEffect(() => {
    rememberSearch(REFERENCE_ROUTE, search);
  }, [search]);

  const go = (next: Partial<ReferenceSearch>) =>
    void navigate({ to: REFERENCE_ROUTE, search: { ...search, ...next } });
  const openReview = (tc: string, snap: string | null) => go({ view: "review", tc, snap });

  return (
    <div className="grid gap-4.5">
      <div className="flex w-fit max-w-full flex-wrap gap-0.5 border-b border-border" role="tablist" aria-label="Reference Answer 보기">
        {TABS.map(({ view, label }) => (
          <button
            key={view}
            type="button"
            role="tab"
            aria-selected={search.view === view}
            onClick={() => go({ view })}
            className={cn(
              "-mb-px border-b-2 border-transparent px-3 py-2 text-[13.5px] whitespace-nowrap text-muted-foreground",
              search.view === view && "border-primary font-semibold text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {search.view === "review" ? (
        <ReferenceReviewTab api={api} reloadKey={reloadKey} focus={search} onFocus={openReview} />
      ) : search.view === "passed" ? (
        <ReferencePassedTab api={api} reloadKey={reloadKey} onOpen={openReview} />
      ) : (
        <ReferenceGenerateTab api={api} reloadKey={reloadKey} prodAccess={prodAccess} onOpenReview={openReview} />
      )}
    </div>
  );
}
